import { Spinner } from '@material-tailwind/react/components/Spinner';
import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { VideoStreamProvider } from 'src/providers/video-stream-provider';
import { useAdminStore } from 'src/stores/useAdminStore';
import { useAuthStore, useCurrentUserStore } from 'src/stores/useUserStore';

const PrivateRouter = lazy(() => import('./private-router'));
const PublicRouter = lazy(() => import('./public-router'));

export function AppRouter() {
  const {
    changeAuthState,
    state: { authToken, isAuthorized },
  } = useAuthStore();
  const { currentUser } = useCurrentUserStore();
  const { adminAuthToken, setAdminAuthToken } = useAdminStore();

  // use case for redirect from farmsort.de
  useEffect(() => {
    const url = new URL(window.location.href);
    const [, authToken] = url.hash.split('authToken=');
    if (authToken) {
      changeAuthState({ authToken });
      // remove token from URL without reloading the page
      window.history.replaceState({}, document.title, '/');
    }
  }, [changeAuthState]);

  // Establishes adminAuthToken the first time we see an admin session (e.g.
  // the farmsort.de hash-redirect flow below). Deliberately a one-time latch:
  // once adminAuthToken is set it must never be re-derived from currentUser
  // again, for two reasons — (1) it must survive mimicking another user, and
  // (2) while mimicking, the backend honors X-Admin-Authorization and reports
  // currentUser as the admin regardless of which user authToken represents,
  // so "currentUser.role === ADMIN" is true for the entire impersonation
  // session and would otherwise keep clobbering adminAuthToken with the
  // mimicked user's token (see useExitMimicUser, which needs the original
  // adminAuthToken intact to let the admin exit impersonation).
  useEffect(() => {
    if (!adminAuthToken && currentUser?.role === 'ADMIN' && authToken) {
      setAdminAuthToken(authToken);
    }
  }, [authToken, currentUser, adminAuthToken, setAdminAuthToken]);

  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div className="flex h-screen w-screen content-center items-center justify-center opacity-50">
            <Spinner className="h-12 w-12" />
          </div>
        }
      >
        <VideoStreamProvider>
          {isAuthorized ? <PrivateRouter /> : <PublicRouter />}
        </VideoStreamProvider>
      </Suspense>
    </BrowserRouter>
  );
}
